import { FC } from 'react';
import { useIntl } from 'react-intl';
import { Typography } from '@material-ui/core';
import { DropzoneArea } from 'material-ui-dropzone';

//Max file size 10mb
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE / 1024 / 1024;
const FILES_LIMIT = 10;

// Mirrors the backend upload allowlist (UserUploadedFileSecurity).
export const ATTACHMENT_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'rtf',
  'txt',
  'xls',
  'xlsx',
  'csv',
  'jpg',
  'jpeg',
  'png',
];
export const SPREADSHEET_EXTENSIONS = ['xls', 'xlsx'];

const hasAllowedExtension = (fileName: string, allowedExtensions: string[]) => {
  const dot = fileName.lastIndexOf('.');
  return (
    dot >= 0 &&
    allowedExtensions.includes(fileName.slice(dot + 1).toLowerCase())
  );
};

const FilesUploadArea: FC<{
  handleFileChange: (files: File[]) => void;
  files?: File[];
  allowedExtensions?: string[];
}> = ({
  handleFileChange,
  files,
  allowedExtensions = ATTACHMENT_EXTENSIONS,
}) => {
  const intl = useIntl();
  const types = allowedExtensions
    .map((extension) => extension.toUpperCase())
    .join(', ');

  return (
    <>
      <DropzoneArea
        initialFiles={files}
        acceptedFiles={allowedExtensions.map((extension) => `.${extension}`)}
        dropzoneText={intl.formatMessage({ id: 'DROPZONE.INSTRUCTION' })}
        showPreviews={true}
        filesLimit={FILES_LIMIT}
        maxFileSize={MAX_FILE_SIZE}
        showPreviewsInDropzone={false}
        useChipsForPreview
        previewGridProps={{ container: { spacing: 1, direction: 'row' } }}
        previewText={intl.formatMessage({ id: 'DROPZONE.SELECTED_FILES' })}
        getDropRejectMessage={(rejectedFile) =>
          intl.formatMessage(
            {
              // The format blocks the upload regardless of size, so report it first.
              id: hasAllowedExtension(rejectedFile.name, allowedExtensions)
                ? 'DROPZONE.REJECTED_TOO_BIG'
                : 'DROPZONE.REJECTED_TYPE',
            },
            { fileName: rejectedFile.name, types, maxSize: MAX_FILE_SIZE_MB },
          )
        }
        getFileLimitExceedMessage={(filesLimit) =>
          intl.formatMessage(
            { id: 'DROPZONE.FILES_LIMIT_EXCEEDED' },
            { maxFiles: filesLimit },
          )
        }
        onChange={handleFileChange}
      />
      <Typography variant="caption" color="textSecondary" component="p">
        {intl.formatMessage(
          { id: 'DROPZONE.ALLOWED_FILES' },
          { types, maxSize: MAX_FILE_SIZE_MB, maxFiles: FILES_LIMIT },
        )}
      </Typography>
    </>
  );
};

export default FilesUploadArea;
